import {HoistModel} from '@xh/hoist/core';
import {isEqual, startCase} from 'lodash';
import {SpecAssistantModel} from '../../spec/assistant/SpecAssistantModel';
import {FormatSpec} from '../../spec/SpecTypes';
import {SelectOption} from '../../gridSpec/configurator/GridSpecConfiguratorModel';
import {ChartSpecModel} from '../ChartSpecModel';
import {ChartSpecAxisChanges, ChartSpecOp, ChartSpecOptions, ChartSpecSeriesChanges, usesSecondaryAxis} from '../ChartSpecOps';
import {AxisId, ChartSpec, OhlcKey} from '../ChartSpecTypes';

/** Format presets offered by the configurator - the assistant (and the JSON editor) can set any FormatSpec. */
export interface FormatPreset {
    value: string;
    label: string;
    format: FormatSpec;
}

export const NUMBER_FORMAT_PRESETS: FormatPreset[] = [
    {value: 'plain', label: 'Plain', format: {type: 'number', numberStyle: 'plain'}},
    {value: 'thousands', label: 'Thousands', format: {type: 'number', numberStyle: 'thousands'}},
    {value: 'millions', label: 'Millions', format: {type: 'number', numberStyle: 'millions'}},
    {value: 'billions', label: 'Billions', format: {type: 'number', numberStyle: 'billions'}},
    {value: 'percent', label: 'Percent', format: {type: 'number', numberStyle: 'percent'}},
    {value: 'price', label: 'Price ($)', format: {type: 'number', numberStyle: 'price', precision: 2, prefix: '$'}}
];

export const DATE_FORMAT_PRESETS: FormatPreset[] = [
    {value: 'YYYY-MM-DD', label: '2025-12-31', format: {type: 'date', dateFmt: 'YYYY-MM-DD'}},
    {value: 'MMM YYYY', label: 'Dec 2025', format: {type: 'date', dateFmt: 'MMM YYYY'}},
    {value: 'YYYY', label: '2025', format: {type: 'date', dateFmt: 'YYYY'}}
];

/**
 * UI state for the chart configurator. Like the grid configurator, it holds no chart configuration
 * of its own: it reads the spec from the ChartSpecModel and turns every edit into ops dispatched
 * with source 'manual' - the same path the AI assistant uses.
 */
export class ChartSpecConfiguratorModel extends HoistModel {
    readonly specModel: ChartSpecModel;
    /** Owned by the ConfigurableChartModel - rendered as the first configurator section. */
    readonly assistantModel: SpecAssistantModel;

    constructor(specModel: ChartSpecModel, assistantModel: SpecAssistantModel) {
        super();
        this.specModel = specModel;
        this.assistantModel = assistantModel;
    }

    get spec(): ChartSpec {
        return this.specModel.spec;
    }

    get isSeriesLayout(): boolean {
        return this.spec.layout === 'series';
    }

    get showsSecondaryAxis(): boolean {
        return usesSecondaryAxis(this.spec);
    }

    get layoutOptions(): SelectOption[] {
        return this.specModel.capabilities.layouts.map(l => ({value: l, label: startCase(l)}));
    }

    get xFieldOptions(): SelectOption[] {
        return this.specModel.catalog.filter(f => f.type !== 'bool').map(f => ({value: f.name, label: this.specModel.fieldLabel(f.name)}));
    }

    /** Numeric fields - any of them can be a candle's open, high, low or close. */
    get numberFieldOptions(): SelectOption[] {
        return this.specModel.catalog.filter(f => f.type === 'number').map(f => ({value: f.name, label: this.specModel.fieldLabel(f.name)}));
    }

    /** Numeric fields not plotted yet, or hidden - adding one shows it. */
    get addableFieldOptions(): SelectOption[] {
        const {specModel} = this;
        return specModel.catalog
            .filter(f => f.type === 'number')
            .filter(f => specModel.getSeries(f.name)?.hidden !== false)
            .map(f => {
                const series = specModel.getSeries(f.name),
                    label = specModel.fieldLabel(f.name);
                return {value: f.name, label: series ? `${label} (hidden)` : label};
            });
    }

    get isXDate(): boolean {
        return this.specModel.getField(this.spec.xField)?.type === 'date';
    }

    setOption<K extends keyof ChartSpecOptions>(key: K, value: ChartSpecOptions[K]) {
        this.dispatch([{op: 'setOptions', options: {[key]: value}}]);
    }

    setOhlcField(key: OhlcKey, field: string) {
        if (!field) return;
        this.dispatch([{op: 'setOptions', options: {ohlc: {...this.spec.ohlc, [key]: field}}}], `candle ${key}: ${this.specModel.fieldLabel(field)}`);
    }

    addSeries(field: string) {
        if (!field) return;
        this.dispatch([{op: 'addSeries', series: {field}}], `add ${this.specModel.fieldLabel(field)}`);
    }

    removeSeries(field: string) {
        this.dispatch([{op: 'removeSeries', field}], `remove ${this.specModel.seriesLabel(field)}`);
    }

    toggleSeriesHidden(field: string) {
        const hidden = !this.specModel.getSeries(field)?.hidden;
        this.dispatch([{op: 'updateSeries', field, changes: {hidden}}], `${hidden ? 'hide' : 'show'} ${this.specModel.seriesLabel(field)}`);
    }

    moveSeries(field: string, toIndex: number) {
        this.dispatch([{op: 'moveSeries', field, toIndex}], `move ${this.specModel.seriesLabel(field)}`);
    }

    /** `typing` coalesces keystrokes in a text input into one undo entry. */
    updateSeries(field: string, changes: ChartSpecSeriesChanges, typing = false) {
        this.dispatch([{op: 'updateSeries', field, changes}], `update ${this.specModel.seriesLabel(field)}`, typing ? `series-${field}` : null);
    }

    updateAxis(axis: AxisId, changes: ChartSpecAxisChanges, typing = false) {
        this.dispatch([{op: 'updateAxis', axis, changes}], `${axis} axis`, typing ? `axis-${axis}` : null);
    }

    /** Replace the axis format with a preset (not merged - a preset is a whole format). */
    setAxisFormat(axis: AxisId, preset: FormatPreset) {
        this.dispatch(
            [{op: 'updateAxis', axis, changes: {format: null}}, ...(preset ? [{op: 'updateAxis', axis, changes: {format: preset.format}} as ChartSpecOp] : [])],
            `${axis} axis format`
        );
    }

    /** The preset matching an axis format, if any. */
    axisPresetValue(axis: AxisId): string {
        const format = this.spec.axes[axis].format;
        if (!format) return null;
        const presets = format.type === 'date' ? DATE_FORMAT_PRESETS : NUMBER_FORMAT_PRESETS;
        return (
            presets.find(p => isEqual(p.format, format))?.value ??
            presets.find(p => (format.type === 'date' ? p.format.dateFmt === format.dateFmt : p.format.numberStyle === format.numberStyle))?.value ??
            null
        );
    }

    private dispatch(ops: ChartSpecOp[], label?: string, coalesceKey?: string) {
        this.specModel.dispatch(ops, {source: 'manual', label, coalesceKey});
    }
}
